// Flight icons, loaded one file at a time from the icons/ folder next to i80.js.
//
// Why not <use href="icons.svg#...">: browsers don't allow <use> to reference an
// SVG on another origin, and products load i80 from design.i80.dk. Fetching each
// icon (served with CORS) and inlining its paths works cross-origin, and a page
// only downloads the icons it uses instead of the whole 1 MB sprite.

import { h } from "./base.js";

let base = new URL("./icons/", import.meta.url);
const cache = new Map();

/** Point icon loading somewhere else, e.g. a self-hosted copy. */
export function setIconBase(url) {
  base = new URL(url, document.baseURI);
  cache.clear();
}

function load(name, size) {
  const key = `${name}-${size}`;
  if (!cache.has(key)) {
    cache.set(
      key,
      fetch(new URL(`${key}.svg`, base))
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`${r.status}`))))
        .then((text) => new DOMParser().parseFromString(text, "image/svg+xml").documentElement)
        .catch((err) => {
          console.warn(`i80: icon "${name}" (${size}px) could not be loaded: ${err.message}`);
          return null;
        }),
    );
  }
  return cache.get(key);
}

/**
 * An <svg class="i80-icon ..."> that fills in its paths when the icon file arrives.
 * Same markup as the Ember I80::Icon component, so components.css applies unchanged.
 */
export function icon(name, { size = "16", stretched = false, className = "", title = null } = {}) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", ["i80-icon", `i80-icon-${name}`, className].filter(Boolean).join(" "));
  svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
  svg.setAttribute("width", stretched ? "100%" : size);
  svg.setAttribute("height", stretched ? "100%" : size);
  svg.setAttribute("fill", "currentColor");
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.setAttribute("data-i80-icon", name);
  if (title) {
    svg.setAttribute("role", "img");
    svg.append(Object.assign(document.createElementNS(svg.namespaceURI, "title"), { textContent: title }));
  } else {
    svg.setAttribute("aria-hidden", "true");
  }
  load(name, size).then((src) => {
    if (!src) return;
    for (const node of src.childNodes) svg.append(document.importNode(node, true));
  });
  return svg;
}

export { h };
